(() => {
  const reduced = () => matchMedia('(prefers-reduced-motion: reduce)').matches;
  async function nativeDrawer() {
    await Promise.race([customElements.whenDefined('cart-drawer'), new Promise((_, reject) => setTimeout(() => reject(new Error('Your bag is still loading. Please try again.')), 12000))]);
    const drawer = document.querySelector('cart-drawer');
    if (!drawer || typeof drawer.renderContents !== 'function') throw new Error('Your bag is still loading. Please try again.');
    return drawer;
  }
  async function countCart() {
    try {
      const response = await fetch((window.routes?.cart_url || '/cart') + '.js', {headers:{Accept:'application/json'}});
      if (!response.ok) return;
      const cart = await response.json();
      document.querySelectorAll('[data-bn-cart-count]').forEach(node => { node.textContent = cart.item_count; });
    } catch (_) {}
  }
  function priceForSet(unitPrice, quantity, duoDiscount) {
    const subtotal = unitPrice * quantity;
    const savings = quantity >= 2 ? Math.min(subtotal, Math.max(0, duoDiscount)) : 0;
    return {subtotal, savings, total:subtotal - savings};
  }
  function discountApplications(cart) {
    return [...(cart.cart_level_discount_applications || []), ...(cart.items || []).flatMap(item => (item.line_level_discount_allocations || []).map(allocation => allocation.discount_application))].filter(Boolean);
  }
  function mergedDiscountCodes(cart, offerCode) {
    const codes = new Map();
    const remember = value => {
      if (typeof value !== 'string' || !value.trim()) return;
      const code = value.trim();
      if (!codes.has(code.toLowerCase())) codes.set(code.toLowerCase(), code);
    };
    (cart.discount_codes || []).forEach(discount => remember(discount.code));
    discountApplications(cart).filter(application => application.type === 'discount_code').forEach(application => remember(application.code || application.title));
    remember(offerCode);
    return [...codes.values()];
  }
  function hasDiscountCode(cart, code) {
    const matches = value => typeof value === 'string' && value.toLowerCase() === code.toLowerCase();
    return (cart.discount_codes || []).some(discount => matches(discount.code) && discount.applicable === true) || discountApplications(cart).some(application => application.type === 'discount_code' && matches(application.code || application.title));
  }
  function hasBetterLineSaving(cart, productId, saving) {
    const items = (cart.items || []).filter(item => String(item.product_id) === productId);
    if (!items.length || items.some(item => !Number.isFinite(item.original_line_price) || !Number.isFinite(item.final_line_price))) return false;
    return items.reduce((total,item) => total + item.original_line_price - item.final_line_price, 0) >= saving;
  }
  function familyInCart(cart, variantId) {
    return (cart.items || []).some(item => String(item.variant_id || item.id) === String(variantId));
  }
  function familyAvailable(product, variantId, planId) {
    const variant = (product.variants || []).find(item => String(item.id) === String(variantId));
    return variant?.available === true && (variant.selling_plan_allocations || []).some(plan => String(plan.selling_plan_id) === String(planId) && plan.price === 0 && (plan.price_adjustments || []).some(adjustment => adjustment.position === 2 && adjustment.price === 3990));
  }
  function familyChargeDate(now = new Date()) {
    const charge = new Date(now);
    charge.setDate(charge.getDate() + 5);
    return charge.toLocaleDateString('en-GB', {day:'numeric',month:'long',year:'numeric'});
  }
  function familyItem(variantId, planId, now = new Date()) {
    return {id:Number(variantId),quantity:1,selling_plan:Number(planId),properties:{_vf_consent_at:now.toISOString(),_vf_consent_via:'join-button',_vf_terms_version:'2026-07-ventra-family-v1',_vf_first_charge:familyChargeDate(now),_vf_variant:'1',_vf_trust:'7'}};
  }
  function notebookItem(formData) {
    const properties = {};
    for (const [key,value] of formData.entries()) {
      const property = key.match(/^properties\[(.+)\]$/);
      if (property) properties[property[1]] = value;
    }
    return {id:Number(formData.get('id')),quantity:Number(formData.get('quantity') || 1),properties};
  }
  function setupSwipe(button, onComplete, canStart) {
    const thumb = button.querySelector('[data-bn-swipe-thumb]');
    let gesture = null;
    function reset() {
      const pointerId = gesture?.pointerId;
      gesture = null;
      button.classList.remove('is-dragging');
      button.style.setProperty('--bn-swipe-x', '0px');
      button.style.setProperty('--bn-swipe-progress', '0');
      if (pointerId !== undefined && button.hasPointerCapture(pointerId)) button.releasePointerCapture(pointerId);
    }
    button.addEventListener('pointerdown', event => {
      if (gesture || !canStart() || event.isPrimary === false || event.button !== 0 || !thumb.contains(event.target)) return;
      const travel = button.clientWidth - thumb.offsetWidth - thumb.offsetLeft * 2;
      if (travel <= 0) return;
      gesture = {pointerId:event.pointerId, startX:event.clientX, startY:event.clientY, travel, horizontal:false};
      try { button.setPointerCapture(event.pointerId); } catch (_) { reset(); }
    });
    button.addEventListener('pointermove', event => {
      if (!gesture || event.pointerId !== gesture.pointerId) return;
      if (!canStart() || (event.pointerType === 'mouse' && event.buttons !== 1)) { reset(); return; }
      const dx = event.clientX - gesture.startX;
      const dy = event.clientY - gesture.startY;
      if (!gesture.horizontal) {
        if (Math.abs(dy) > 10 && Math.abs(dy) >= Math.abs(dx)) { reset(); return; }
        if (Math.abs(dx) < 8 || Math.abs(dx) < Math.abs(dy) * 1.2) return;
        gesture.horizontal = true;
        button.classList.add('is-dragging');
      }
      if (event.cancelable) event.preventDefault();
      const distance = Math.max(0, Math.min(gesture.travel, dx));
      button.style.setProperty('--bn-swipe-x', distance + 'px');
      button.style.setProperty('--bn-swipe-progress', String(distance / gesture.travel));
    });
    button.addEventListener('pointerup', event => {
      if (!gesture || event.pointerId !== gesture.pointerId) return;
      const distance = Math.max(0, Math.min(gesture.travel, event.clientX - gesture.startX));
      const complete = gesture.horizontal && distance >= gesture.travel * .85 && canStart();
      reset();
      if (complete) onComplete();
    });
    for (const type of ['pointercancel', 'lostpointercapture']) {
      button.addEventListener(type, event => { if (gesture?.pointerId === event.pointerId) reset(); });
    }
    button.addEventListener('keydown', event => {
      if (event.key === 'Escape' && gesture) { event.preventDefault(); reset(); }
    });
    button.addEventListener('click', event => {
      event.preventDefault();
      if (event.detail === 0 && !gesture && canStart()) onComplete();
    });
    button.addEventListener('blur', reset);
    return {reset};
  }
  function init() {
    const root = document.querySelector('[data-bn-root]');
    if (!root || root.dataset.initialized) return;
    root.dataset.initialized = 'true';
    const track = root.querySelector('[data-bn-track]');
    const slides = [...root.querySelectorAll('[data-bn-slide]')];
    const dots = [...root.querySelectorAll('[data-bn-dot]')];
    const experience = root.querySelector('.bn-experience');
    const intro = root.querySelector('.bn-intro');
    const dock = root.querySelector('[data-bn-dock]');
    const dialog = root.querySelector('[data-bn-dialog]');
    const purchaseTrigger = root.querySelector('[data-bn-order-open]');
    const form = root.querySelector('.bn-form');
    const add = root.querySelector('[data-bn-add]');
    const status = root.querySelector('[data-bn-status]');
    const familyCard = root.querySelector('[data-bn-family]');
    const checkout = root.querySelector('[data-bn-checkout]');
    const familyFine = root.querySelector('[data-bn-family-fine]');
    const familyBilling = familyFine?.querySelector('[data-bn-family-billing]');
    const familyNote = root.querySelector('[data-bn-family-status]');
    const skipFamily = root.querySelector('[data-bn-skip-family]');
    const addLabel = add?.querySelector('[data-bn-add-label]');
    const addJoin = add?.querySelector('[data-bn-add-join]');
    const familyState = {member:familyCard?.dataset.member === 'true',ready:false,checking:false,inCart:false};
    let familyCheck = 0;
    let submitter = null;
    let familyIntent = null;
    let submitMode = null;
    const variants = JSON.parse(root.querySelector('[data-bn-variants]').textContent || '[]') || [];
    let position = 0;
    let tick = false;
    let countTimer;
    let pendingAddition = false;
    let submitting = false;
    const money = cents => new Intl.NumberFormat(document.documentElement.lang || 'en', {style:'currency',currency:root.dataset.currency || 'USD'}).format(cents / 100);
    const savingMoney = cents => new Intl.NumberFormat(document.documentElement.lang || 'en', {style:'currency',currency:root.dataset.currency || 'USD',minimumFractionDigits:0,maximumFractionDigits:2}).format(cents / 100);
    const duoDiscount = Math.max(0, Math.round(Number(root.dataset.duoDiscount) || 0));
    const variant = () => variants.find(item => String(item.id) === form?.querySelector('[name="id"]')?.value);
    const enter = intro.querySelector('[data-bn-enter]');
    const motionPreference = matchMedia('(prefers-reduced-motion: reduce)');
    let introVisible = intro.getBoundingClientRect().bottom > 0 && intro.getBoundingClientRect().top < innerHeight;
    let entering = false;
    let entryTimer;
    function syncIntroMotion() {
      intro.classList.toggle('is-motion', !motionPreference.matches);
      intro.classList.toggle('is-paused', document.hidden || !introVisible);
      const film = intro.querySelector('video');
      if (film) {
        if (document.hidden || !introVisible || motionPreference.matches) film.pause();
        else film.play().catch(() => {});
      }
    }
    function finishEntry() {
      clearTimeout(entryTimer);
      if (location.hash !== enter.hash) history.pushState(history.state, '', enter.hash);
      experience.scrollIntoView({behavior:'instant',block:'start'});
      experience.focus({preventScroll:true});
      intro.classList.remove('is-entering');
      entering = false;
      introVisible = false;
      syncIntroMotion();
      requestScene();
    }
    enter.addEventListener('click', event => {
      if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || (enter.target && enter.target !== '_self')) return;
      event.preventDefault();
      if (entering) return;
      if (motionPreference.matches) { finishEntry(); return; }
      entering = true;
      intro.classList.add('is-entering');
      entryTimer = setTimeout(finishEntry, 620);
    });
    const introObserver = new IntersectionObserver(entries => {
      introVisible = entries[0].isIntersecting;
      syncIntroMotion();
    }, {threshold:0});
    introObserver.observe(intro);
    motionPreference.addEventListener('change', () => {
      if (motionPreference.matches && entering) finishEntry();
      syncIntroMotion();
    });
    window.addEventListener('pageshow', event => {
      if (!event.persisted) return;
      clearTimeout(entryTimer);
      entering = false;
      intro.classList.remove('is-entering');
      introVisible = intro.getBoundingClientRect().bottom > 0 && intro.getBoundingClientRect().top < innerHeight;
      syncIntroMotion();
    });
    syncIntroMotion();
    function drawScene() {
      tick = false;
      const top = experience.getBoundingClientRect().top;
      const progress = Math.max(0, Math.min(1, -top / 300));
      experience.style.setProperty('--bn-progress', progress.toFixed(3));
      const visible = intro.getBoundingClientRect().bottom < window.innerHeight * .4;
      dock.classList.toggle('is-visible', visible);
      dock.setAttribute('aria-hidden', String(!visible));
      purchaseTrigger.tabIndex = visible ? 0 : -1;
      if (!visible) swipe.reset();
    }
    function requestScene() { if (!tick) { tick = true; requestAnimationFrame(drawScene); } }
    function select(index) {
      if (!slides.length) return;
      position = (index + slides.length) % slides.length;
      track.scrollTo({left:position * track.clientWidth,behavior:reduced() ? 'auto':'smooth'});
    }
    function syncScene() {
      position = Math.max(0, Math.min(slides.length - 1, Math.round(track.scrollLeft / track.clientWidth)));
      dots.forEach((dot,index) => index === position ? dot.setAttribute('aria-current','true') : dot.removeAttribute('aria-current'));
      root.querySelector('[data-bn-detail="cover"]').hidden = position !== 0;
      root.querySelector('[data-bn-detail="interior"]').hidden = position === 0;
      slides.forEach((slide,index) => { if (index !== position) slide.querySelector('video')?.pause(); });
    }
    root.querySelector('[data-bn-next]').addEventListener('click', () => select(position + 1));
    root.querySelector('[data-bn-prev]').addEventListener('click', () => select(position - 1));
    dots.forEach((dot,index) => dot.addEventListener('click', () => select(index)));
    track.addEventListener('scroll',syncScene,{passive:true});
    track.addEventListener('keydown',event => { if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') { event.preventDefault(); select(position + (event.key === 'ArrowRight' ? 1:-1)); } });
    function closeOrder() { if (dialog.open) dialog.close(); document.body.classList.remove('bn-sheet-open'); }
    function openOrder() {
      if (dialog.open) return;
      if (status && !pendingAddition) status.textContent = '';
      dialog.showModal();
      document.body.classList.add('bn-sheet-open');
      if (!pendingAddition && !submitting) refreshFamily();
    }
    const swipe = setupSwipe(purchaseTrigger, openOrder, () => dock.classList.contains('is-visible') && !dialog.open && !purchaseTrigger.disabled);
    root.querySelector('[data-bn-order-close]').addEventListener('click',closeOrder);
    dialog.addEventListener('close',() => { document.body.classList.remove('bn-sheet-open'); swipe.reset(); });
    dialog.addEventListener('click',event => { if (event.target !== dialog) return; const bounds = dialog.getBoundingClientRect(); if (event.clientX < bounds.left || event.clientX > bounds.right || event.clientY < bounds.top || event.clientY > bounds.bottom) closeOrder(); });
    // Family is offered only once availability is confirmed, never to members,
    // never twice, and not while a retry of an earlier addition is pending.
    function familyOffered() {
      return !!familyCard && familyState.ready && !familyState.member && !familyState.inCart && !pendingAddition;
    }
    function syncFamily(offered, available) {
      if (!familyCard) return;
      checkout.classList.toggle('is-family', offered);
      familyCard.hidden = !offered;
      familyFine.hidden = !offered;
      skipFamily.hidden = !offered;
      skipFamily.disabled = submitting || !available;
      skipFamily.textContent = submitting && submitMode === 'skip' ? root.dataset.adding : skipFamily.dataset.label;
      familyBilling.textContent = familyCard.dataset.billing.replace('[date]',familyChargeDate());
      if (offered) add.setAttribute('aria-describedby',familyBilling.id); else add.removeAttribute('aria-describedby');
      familyNote.textContent = familyState.member ? familyCard.dataset.memberText : (familyState.inCart ? familyCard.dataset.inCartText : '');
    }
    async function familySnapshot() {
      const [productResponse,cartResponse] = await Promise.all([
        fetch(familyCard.dataset.productUrl + '.js',{headers:{Accept:'application/json'},cache:'no-store'}),
        fetch(root.dataset.cartUrl + '.js',{headers:{Accept:'application/json'},cache:'no-store'})
      ]);
      if (!productResponse.ok || !cartResponse.ok) throw new Error(familyCard.dataset.unavailableText);
      const [product,cart] = await Promise.all([productResponse.json(),cartResponse.json()]);
      if (!familyAvailable(product,familyCard.dataset.variant,familyCard.dataset.plan)) throw new Error(familyCard.dataset.unavailableText);
      return cart;
    }
    async function refreshFamily() {
      if (!familyCard || familyState.member || familyState.checking) { syncOffer(); return; }
      const check = ++familyCheck;
      familyState.checking = true;
      try {
        const cart = await familySnapshot();
        // A submission that started meanwhile owns the cart state; drop this stale result.
        if (check === familyCheck) {
          familyState.ready = true;
          familyState.inCart = familyInCart(cart,familyCard.dataset.variant);
        }
      } catch (_) { if (check === familyCheck) familyState.ready = false; }
      finally { familyState.checking = false;syncOffer(); }
    }
    async function prepareAddition(formData, wantsFamily) {
      if (!wantsFamily || !familyCard || familyState.member) return {body:formData,headers:{Accept:'application/json','X-Requested-With':'XMLHttpRequest'},familyAdded:false};
      // Re-check immediately before adding: another tab may already have added Family.
      let cart;
      try { cart = await familySnapshot(); }
      catch (error) { familyState.ready = false; throw error; }
      familyState.inCart = familyInCart(cart,familyCard.dataset.variant);
      if (familyState.inCart) return {body:formData,headers:{Accept:'application/json','X-Requested-With':'XMLHttpRequest'},familyAdded:false};
      return {body:JSON.stringify({items:[notebookItem(formData),familyItem(familyCard.dataset.variant,familyCard.dataset.plan)]}),headers:{Accept:'application/json','Content-Type':'application/json'},familyAdded:true};
    }
    function syncOffer() {
      const current = variant();
      if (!current || !add) return;
      const quantity = Number(form.querySelector('[name="quantity"]:checked')?.value || 1);
      const duo = priceForSet(current.price, 2, duoDiscount);
      const selectedSet = priceForSet(current.price, quantity, duoDiscount);
      root.querySelectorAll('[data-bn-price]').forEach(node => { node.textContent = money(current.price); });
      root.querySelector('[data-bn-single]').textContent = money(current.price);
      root.querySelector('[data-bn-duo]').textContent = money(duo.total);
      const originalPrice = root.querySelector('[data-bn-duo-original]');
      originalPrice.textContent = money(duo.subtotal);
      originalPrice.hidden = duo.savings === 0;
      const savings = root.querySelector('[data-bn-duo-saving]');
      savings.textContent = root.dataset.duoSavingLabel.replace('[amount]', savingMoney(duo.savings));
      savings.hidden = duo.savings === 0;
      const total = root.querySelector('[data-bn-total]');
      total.textContent = money(selectedSet.total);
      total.hidden = pendingAddition;
      form.querySelector('.bn-offers').disabled = submitting || pendingAddition;
      const variantSelect = form.querySelector('select[name="id"]');
      if (variantSelect) variantSelect.disabled = submitting || pendingAddition;
      const offered = familyOffered() && current.available;
      add.disabled = submitting || (!pendingAddition && !current.available);
      const busy = submitting && submitMode !== 'skip';
      addLabel.textContent = busy ? root.dataset.adding : (pendingAddition ? root.dataset.offerRetry : (current.available ? root.dataset.cta : root.dataset.unavailable));
      // The second line is what tells the shopper this button also joins Family.
      if (addJoin) addJoin.hidden = !offered || busy;
      syncFamily(offered, current.available);
    }
    async function renderAndOpen(drawer, result) {
      const sections = drawer.getSectionsToRender().map(section => section.id);
      if (!result.sections || sections.some(id => !result.sections[id])) {
        const url = new URL(location.href);
        url.searchParams.set('sections', sections.join(','));
        const response = await fetch(url,{headers:{Accept:'application/json'}});
        if (!response.ok) throw new Error('Your bag could not refresh. Please try opening it again.');
        result.sections = await response.json();
        if (sections.some(id => !result.sections[id])) throw new Error('Your bag could not refresh. Please try opening it again.');
      }
      drawer.classList.remove('is-empty');
      drawer.setActiveElement?.(purchaseTrigger);
      drawer.renderContents(result,true);
      closeOrder();
      drawer.open(purchaseTrigger);
      countCart();
    }
    async function applyDuoOffer(drawer) {
      const snapshotResponse = await fetch(root.dataset.cartUrl + '.js', {headers:{Accept:'application/json'},cache:'no-store'});
      if (!snapshotResponse.ok) throw new Error(root.dataset.discountError);
      const cart = await snapshotResponse.json();
      if (duoDiscount === 0) return cart;
      const code = root.dataset.duoCode.trim();
      if (!code) throw new Error(root.dataset.discountError);
      const response = await fetch(root.dataset.cartUpdate + '.js', {
        method:'POST',
        headers:{Accept:'application/json','Content-Type':'application/json'},
        body:JSON.stringify({discount:mergedDiscountCodes(cart, code).join(','),sections:drawer.getSectionsToRender().map(section => section.id),sections_url:location.pathname})
      });
      const updated = await response.json();
      if (!response.ok || updated.status) throw new Error(root.dataset.discountError);
      const eligibleQuantity = (updated.items || []).filter(item => String(item.product_id) === root.dataset.productId).reduce((quantity,item) => quantity + item.quantity, 0);
      if (eligibleQuantity >= 2 && !hasDiscountCode(updated, code) && !hasBetterLineSaving(updated, root.dataset.productId, duoDiscount)) throw new Error(root.dataset.discountError);
      return updated;
    }
    form?.addEventListener('change',syncOffer);
    // Keep the choice shown when activation began, even if availability finishes mid-click.
    const captureFamilyIntent = () => { familyIntent = familyOffered() && !!variant()?.available && !!addJoin && !addJoin.hidden; };
    add?.addEventListener('pointerdown',captureFamilyIntent);
    add?.addEventListener('keydown',event => { if (!event.repeat && (event.key === 'Enter' || event.key === ' ')) captureFamilyIntent(); });
    add?.addEventListener('pointercancel',() => { familyIntent = null; });
    add?.addEventListener('blur',() => { familyIntent = null; });
    // Track which button submitted, for browsers without SubmitEvent.submitter.
    add?.addEventListener('click',() => { submitter = add; if (familyIntent === null) captureFamilyIntent(); });
    skipFamily?.addEventListener('click',() => { submitter = skipFamily; familyIntent = false; });
    form?.addEventListener('submit',async event => {
      event.preventDefault();
      const via = event.submitter || submitter;
      submitter = null;
      if (submitting || !via || via.disabled) return;
      const requestBody = pendingAddition ? null : new FormData(form);
      // Only the main button, while it is labelled as joining Family, adds the membership.
      const wantsFamily = via === add && familyIntent === true && familyOffered() && !!variant()?.available;
      familyIntent = null;
      submitMode = via === skipFamily ? 'skip' : 'add';
      submitting = true;
      familyCheck++;
      syncOffer();
      via.setAttribute('aria-busy','true');
      status.textContent = '';
      let offerApplied = false;
      try {
        const drawer = await nativeDrawer();
        if (!pendingAddition) {
          const request = await prepareAddition(requestBody,wantsFamily);
          const response = await fetch(root.dataset.cartAdd + '.js',{method:'POST',headers:request.headers,body:request.body});
          const result = await response.json();
          if (!response.ok || result.status) throw new Error(result.description || result.message || root.dataset.error);
          pendingAddition = true;
          if (request.familyAdded) familyState.inCart = true;
        }
        const updated = await applyDuoOffer(drawer);
        offerApplied = true;
        await renderAndOpen(drawer,updated);
        pendingAddition = false;
        status.textContent = root.dataset.added;
      } catch (error) {
        status.textContent = pendingAddition ? (offerApplied ? root.dataset.bagError : root.dataset.discountError) : (error.message || root.dataset.error);
      } finally { submitting = false; submitMode = null; via.removeAttribute('aria-busy'); syncOffer(); }
    });
    root.querySelector('[data-bn-cart-open]').addEventListener('click',async event => {
      const trigger = event.currentTarget;
      try { const drawer = await nativeDrawer();closeOrder();drawer.setActiveElement?.(trigger);drawer.open(trigger);countCart(); }
      catch (error) { dialog.showModal();document.body.classList.add('bn-sheet-open');status.textContent = error.message; }
    });
    const cartWrapper = document.querySelector('.vfm-native-cart');
    if (cartWrapper) new MutationObserver(() => { clearTimeout(countTimer);countTimer = setTimeout(countCart,180); }).observe(cartWrapper,{childList:true,subtree:true});
    const demo = root.querySelector('.bn-demo');
    demo?.addEventListener('toggle',() => { if (!demo.open) demo.querySelector('video')?.pause(); });
    window.addEventListener('scroll',requestScene,{passive:true});
    window.addEventListener('resize',() => { swipe.reset(); requestScene(); });
    window.addEventListener('pageshow',() => { swipe.reset(); requestScene(); });
    window.addEventListener('blur',swipe.reset);
    document.addEventListener('visibilitychange',() => { if (document.hidden) swipe.reset(); if (document.hidden && entering) finishEntry(); syncIntroMotion(); if (!document.hidden) requestScene(); });
    syncScene();syncOffer();drawScene();
    // Check Family availability up front so the popup opens in its final state.
    refreshFamily();
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded',init); else init();
  document.addEventListener('shopify:section:load',init);
})();
